// src/pages/vendor/Coupons.jsx
import React, { useState, useEffect } from 'react';
import { vendorApi } from '../../api/vendorApi';
import {
  Box, Paper, Typography, Button, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,
  FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel, Grid
} from '@mui/material';
import { Add, Edit, Delete, Refresh, CheckCircle, Cancel } from '@mui/icons-material';

const VendorCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formData, setFormData] = useState({
    code: '', type: 'percentage', value: '', minOrder: 0, maxDiscount: '',
    startDate: '', endDate: '', usageLimit: 1, isActive: true
  });

  const fetchCoupons = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await vendorApi.getCoupons();
      
      let couponArray = [];
      if (Array.isArray(data)) {
        couponArray = data;
      } else if (data && typeof data === 'object') {
        couponArray = data.data || data.coupons || (data.id ? [data] : []);
      }
      
      setCoupons(couponArray);
    } catch (err) {
      setError('Failed to load coupons.');
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCoupons(); }, []);

  const handleOpenDialog = (coupon = null) => {
    if (coupon) {
      setEditingCoupon(coupon);
      setFormData({
        code: coupon.code, type: coupon.type, value: coupon.value,
        minOrder: coupon.minOrder, maxDiscount: coupon.maxDiscount || '',
        startDate: coupon.startDate ? coupon.startDate.split('T')[0] : '',
        endDate: coupon.endDate ? coupon.endDate.split('T')[0] : '',
        usageLimit: coupon.usageLimit, isActive: coupon.isActive
      });
    } else {
      setEditingCoupon(null);
      setFormData({ code: '', type: 'percentage', value: '', minOrder: 0, maxDiscount: '', startDate: '', endDate: '', usageLimit: 1, isActive: true });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => { setOpenDialog(false); setError(null); };

  const handleSave = async () => {
    try {
      if (editingCoupon) {
        await vendorApi.updateCoupon(editingCoupon.id, formData);
        setSuccess('Coupon updated successfully!');
      } else {
        await vendorApi.createCoupon(formData);
        setSuccess('Coupon created successfully!');
      }
      handleCloseDialog();
      fetchCoupons();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save coupon');
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      await vendorApi.toggleCoupon(id);
      setSuccess(`Coupon ${currentStatus ? 'deactivated' : 'activated'}!`);
      fetchCoupons();
    } catch (err) { setError('Failed to toggle coupon status.'); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this coupon?')) {
      try {
        await vendorApi.deleteCoupon(id);
        setSuccess('Coupon deleted.');
        fetchCoupons();
      } catch (err) { setError('Failed to delete coupon.'); }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Never';
      return date.toLocaleDateString();
    } catch {
      return 'Never';
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h5" fontWeight="bold">Coupons</Typography>
            <Typography variant="body2" color="text.secondary">Create and manage your discount codes</Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<Refresh />} onClick={fetchCoupons}>Refresh</Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>Add Coupon</Button>
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Min Order</TableCell>
                <TableCell>Used / Limit</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></TableCell></TableRow>
              ) : coupons.length === 0 ? (
                <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>No coupons created yet.</TableCell></TableRow>
              ) : (
                coupons.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell><Typography fontWeight="bold">{c.code}</Typography></TableCell>
                    <TableCell>{c.type === 'percentage' ? `${c.value}%` : `$${c.value}`}</TableCell>
                    <TableCell>${c.minOrder}</TableCell>
                    <TableCell>{c.usedCount || 0} / {c.usageLimit || '∞'}</TableCell>
                    <TableCell>{formatDate(c.endDate)}</TableCell>
                    <TableCell><Chip icon={c.isActive ? <CheckCircle /> : <Cancel />} label={c.isActive ? 'Active' : 'Inactive'} size="small" color={c.isActive ? 'success' : 'error'} /></TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenDialog(c)}><Edit /></IconButton>
                      <IconButton size="small" color="warning" onClick={() => handleToggle(c.id, c.isActive)}><Refresh /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(c.id)}><Delete /></IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField fullWidth label="Coupon Code" value={formData.code} onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})} required disabled={!!editingCoupon} />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})} label="Type">
                    <MenuItem value="percentage">Percentage (%)</MenuItem>
                    <MenuItem value="fixed">Fixed ($)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth type="number" label="Value" value={formData.value} onChange={(e) => setFormData({...formData, value: parseFloat(e.target.value)})} required />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth type="number" label="Min Order ($)" value={formData.minOrder} onChange={(e) => setFormData({...formData, minOrder: parseFloat(e.target.value)})} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth type="number" label="Max Discount ($)" value={formData.maxDiscount} onChange={(e) => setFormData({...formData, maxDiscount: parseFloat(e.target.value)})} />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth type="date" label="Start Date" value={formData.startDate} onChange={(e) => setFormData({...formData, startDate: e.target.value})} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth type="date" label="End Date" value={formData.endDate} onChange={(e) => setFormData({...formData, endDate: e.target.value})} InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>

            <TextField fullWidth type="number" label="Usage Limit (Leave empty for unlimited)" value={formData.usageLimit} onChange={(e) => setFormData({...formData, usageLimit: parseInt(e.target.value)})} />
            
            {editingCoupon && (
              <FormControlLabel control={<Switch checked={formData.isActive} onChange={(e) => setFormData({...formData, isActive: e.target.checked})} />} label="Active" />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>{editingCoupon ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorCoupons;